import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

/* ---------- Slide 1: START — incorporation docs, checks, seal ---------- */
function SceneStart({ active }) {
  const k = active ? "run" : "idle";
  return (
    <svg viewBox="0 0 520 420" className="h-full w-full" role="img" aria-label="Company incorporation motion graphic">
      <defs>
        <linearGradient id="docGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#ffffff" /><stop offset="100%" stopColor="#EAF2FF" />
        </linearGradient>
        <linearGradient id="blueGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0B5FFF" /><stop offset="100%" stopColor="#4E9CFF" />
        </linearGradient>
      </defs>
      <g key={k}>
        <rect x="70" y="60" width="300" height="330" rx="18" fill="#0B5FFF" opacity="0.06" className="anim-float" style={{ animationDelay: "0.8s" }} />
        <rect x="95" y="40" width="300" height="330" rx="18" fill="url(#docGrad)" stroke="#0B5FFF" strokeOpacity="0.18" strokeWidth="1.5" />
        <text x="125" y="86" fontFamily="Plus Jakarta Sans, sans-serif" fontSize="17" fontWeight="800" fill="#0A1F44">Certificate of</text>
        <text x="125" y="108" fontFamily="Plus Jakarta Sans, sans-serif" fontSize="17" fontWeight="800" fill="#0B5FFF">Incorporation</text>
        {[150, 178, 206].map((y, i) => (
          <g key={y}>
            <circle cx="138" cy={y - 5} r="9" fill="#0B5FFF" opacity="0.12" />
            <path d={`M133 ${y - 5} l4 4 l7 -8`} stroke="#0B5FFF" strokeWidth="2.5" fill="none" strokeLinecap="round"
              className="anim-draw" style={{ "--dash": 24, animationDelay: `${0.4 + i * 0.35}s` }} />
            <rect x="158" y={y - 11} width={140 - i * 24} height="9" rx="4.5" fill="#0A1F44" opacity="0.14" />
          </g>
        ))}
        <rect x="125" y="240" width="240" height="60" rx="12" fill="#EAF2FF" />
        <text x="141" y="266" fontFamily="Plus Jakarta Sans, sans-serif" fontSize="12" fontWeight="700" fill="#0B5FFF">CIN: U74999TG2026PTC</text>
        <text x="141" y="287" fontFamily="Inter, sans-serif" fontSize="11" fill="#5B6472">DIN · PAN · TAN · GST — bundled</text>
        <g className="anim-stamp" style={{ animationDelay: "1.6s" }}>
          <circle cx="330" cy="300" r="42" fill="none" stroke="url(#blueGrad)" strokeWidth="4" />
          <circle cx="330" cy="300" r="32" fill="none" stroke="#0B5FFF" strokeWidth="1.5" strokeDasharray="4 4" />
          <text x="330" y="305" textAnchor="middle" fontFamily="Plus Jakarta Sans, sans-serif" fontSize="12" fontWeight="800" fill="#0B5FFF">APPROVED</text>
        </g>
        <g className="anim-pop" style={{ animationDelay: "2.1s" }}>
          <rect x="330" y="80" width="120" height="52" rx="26" fill="url(#blueGrad)" />
          <path d="M350 106 l8 8 l16 -18" stroke="#fff" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          <text x="395" y="111" textAnchor="middle" fontFamily="Plus Jakarta Sans, sans-serif" fontSize="13" fontWeight="700" fill="#fff">Day 7</text>
        </g>
        <circle cx="90" cy="330" r="5" fill="#4E9CFF" className="anim-glow" />
        <circle cx="440" cy="180" r="4" fill="#0B5FFF" className="anim-glow" style={{ animationDelay: "1s" }} />
        <circle cx="420" cy="360" r="6" fill="#4E9CFF" opacity="0.5" className="anim-glow" style={{ animationDelay: "2s" }} />
      </g>
    </svg>
  );
}

/* ---------- Slide 2: SCALE — CFO dashboard, rising charts ---------- */
function SceneScale({ active }) {
  const k = active ? "run" : "idle";
  const bars = [46, 74, 60, 98, 120, 146];
  return (
    <svg viewBox="0 0 520 420" className="h-full w-full" role="img" aria-label="Virtual CFO dashboard motion graphic">
      <defs>
        <linearGradient id="barGrad" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#0B5FFF" stopOpacity="0.25" /><stop offset="100%" stopColor="#0B5FFF" />
        </linearGradient>
      </defs>
      <g key={k}>
        <rect x="40" y="36" width="440" height="348" rx="20" fill="#ffffff" stroke="#0B5FFF" strokeOpacity="0.15" strokeWidth="1.5" />
        <rect x="40" y="36" width="440" height="52" rx="20" fill="#EAF2FF" />
        <circle cx="72" cy="62" r="6" fill="#0B5FFF" opacity="0.35" />
        <circle cx="92" cy="62" r="6" fill="#0B5FFF" opacity="0.2" />
        <text x="120" y="68" fontFamily="Plus Jakarta Sans, sans-serif" fontSize="13" fontWeight="700" fill="#0A1F44">CFO Dashboard — Q3</text>
        <g className="anim-pop" style={{ animationDelay: "2.7s" }}>
          <rect x="344" y="47" width="118" height="32" rx="16" fill="#0A1F44" />
          <text x="403" y="68" textAnchor="middle" fontFamily="Plus Jakarta Sans, sans-serif" fontSize="12" fontWeight="700" fill="#4E9CFF">+38% QoQ</text>
        </g>
        {[["Runway", "18 mo"], ["MRR", "₹84L"], ["Burn", "-12%"]].map(([label, val], i) => (
          <g key={label} className="anim-pop" style={{ animationDelay: `${0.3 + i * 0.25}s` }}>
            <rect x={64 + i * 136} y="108" width="120" height="52" rx="12" fill="#EAF2FF" opacity="0.8" />
            <text x={76 + i * 136} y="130" fontFamily="Inter, sans-serif" fontSize="10" fill="#5B6472">{label}</text>
            <text x={76 + i * 136} y="150" fontFamily="Plus Jakarta Sans, sans-serif" fontSize="16" fontWeight="800" fill="#0B5FFF">{val}</text>
          </g>
        ))}
        <line x1="64" y1="356" x2="470" y2="356" stroke="#0A1F44" strokeOpacity="0.12" strokeWidth="1.5" />
        {bars.map((h, i) => (
          <rect key={i} x={76 + i * 64} y={356 - h} width="34" height={h} rx="7" fill={i === bars.length - 1 ? "#0B5FFF" : "url(#barGrad)"}
            className="anim-bar" style={{ animationDelay: `${0.5 + i * 0.18}s` }} opacity={i === bars.length - 1 ? 1 : 0.75} />
        ))}
        <path d="M64 318 C 140 306, 190 288, 252 266 S 360 226, 428 200" fill="none" stroke="#4E9CFF" strokeWidth="4" strokeLinecap="round"
          className="anim-draw" style={{ "--dash": 520, animationDelay: "1.2s" }} />
        <g className="anim-pop" style={{ animationDelay: "2.4s" }}>
          <circle cx="428" cy="200" r="8" fill="#0B5FFF" stroke="#ffffff" strokeWidth="3" />
          <circle cx="428" cy="200" r="15" fill="none" stroke="#0B5FFF" strokeOpacity="0.3" strokeWidth="2" />
        </g>
        <text x="64" y="382" fontFamily="Inter, sans-serif" fontSize="10" fill="#5B6472">Apr</text>
        <text x="440" y="382" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="10" fill="#5B6472">Sep</text>
      </g>
    </svg>
  );
}

/* ---------- Slide 3: RAISE — growth curve to the growth stage ---------- */
function SceneRaise({ active }) {
  const k = active ? "run" : "idle";
  const nodes = [
    { x: 90, y: 330, label: "Seed" },
    { x: 210, y: 268, label: "Pre-A" },
    { x: 330, y: 176, label: "Series A" },
    { x: 440, y: 74, label: "Growth" },
  ];
  return (
    <svg viewBox="0 0 520 420" className="h-full w-full" role="img" aria-label="Fundraising growth curve motion graphic">
      <defs>
        <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0B5FFF" stopOpacity="0.28" /><stop offset="100%" stopColor="#0B5FFF" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="curveGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#4E9CFF" /><stop offset="100%" stopColor="#0B5FFF" />
        </linearGradient>
      </defs>
      <g key={k}>
        {[340, 280, 220, 160, 100].map((y) => (
          <line key={y} x1="50" y1={y} x2="480" y2={y} stroke="#0B5FFF" strokeOpacity="0.08" strokeWidth="1" />
        ))}
        <path d="M60 350 C 160 345, 230 300, 300 230 S 420 90, 460 62 L460 380 L60 380 Z" fill="url(#areaGrad)"
          className="anim-pop" style={{ animationDelay: "1.4s" }} />
        <path d="M60 350 C 160 345, 230 300, 300 230 S 420 90, 460 62" fill="none" stroke="url(#curveGrad)" strokeWidth="5" strokeLinecap="round"
          className="anim-draw" style={{ "--dash": 640, animationDelay: "0.4s", animationDuration: "2.4s" }} />
        {nodes.map((n, i) => (
          <g key={n.label} className="anim-pop" style={{ animationDelay: `${0.6 + i * 0.45}s` }}>
            <circle cx={n.x} cy={n.y} r={i === nodes.length - 1 ? 10 : 7} fill="#0B5FFF" stroke="#fff" strokeWidth="3" />
            {i === nodes.length - 1 && <circle cx={n.x} cy={n.y} r="20" fill="none" stroke="#0B5FFF" strokeOpacity="0.35" strokeWidth="2" className="anim-glow" />}
          </g>
        ))}
        {nodes.map((n, i) => (
          <text key={n.label} x={n.x} y={n.y - 22} textAnchor="middle" fontFamily="Plus Jakarta Sans, sans-serif"
            fontSize={i === nodes.length - 1 ? 15 : 11} fontWeight={i === nodes.length - 1 ? 800 : 600}
            fill={i === nodes.length - 1 ? "#0B5FFF" : "#5B6472"} className="anim-pop" style={{ animationDelay: `${0.8 + i * 0.45}s` }}>
            {n.label}
          </text>
        ))}
        <g className="anim-float">
          <g className="anim-pop" style={{ animationDelay: "2.6s" }}>
            <rect x="316" y="330" width="150" height="46" rx="23" fill="#0A1F44" />
            <text x="391" y="352" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="10" fill="#9FB6DA">Term sheet signed</text>
            <text x="391" y="367" textAnchor="middle" fontFamily="Plus Jakarta Sans, sans-serif" fontSize="12" fontWeight="800" fill="#4E9CFF">₹58 Cr · Series A</text>
          </g>
        </g>
        <g className="anim-pop" style={{ animationDelay: "2.2s" }}>
          <rect x="56" y="52" width="118" height="40" rx="20" fill="#EAF2FF" />
          <text x="115" y="77" textAnchor="middle" fontFamily="Plus Jakarta Sans, sans-serif" fontSize="12" fontWeight="700" fill="#0B5FFF">Valuation ✓</text>
        </g>
      </g>
    </svg>
  );
}

const SLIDES = [
  {
    id: "start",
    kicker: "01 · Start",
    headline: ["From Idea to", "Incorporation — in", "Days, Not Months"],
    sub: "Private Limited, LLP, GST and every registration bundled into one engagement — so you launch while others queue.",
    Scene: SceneStart,
  },
  {
    id: "scale",
    kicker: "02 · Scale",
    headline: ["Your Virtual CFO &", "Compliance Partner,", "On Demand"],
    sub: "Board-level finance, ROC, tax and audit run by 40+ professionals — the discipline investors expect, without the full-time cost.",
    Scene: SceneScale,
  },
  {
    id: "raise",
    kicker: "03 · Raise",
    headline: ["Fundraising-Ready:", "From Valuation to", "Term Sheet"],
    sub: "₹550+ Cr valued, investor-grade decks and warm introductions — for rounds that change the trajectory of your company.",
    Scene: SceneRaise,
  },
];

export default function Hero() {
  const [idx, setIdx] = useState(0);
  const go = useCallback((i) => setIdx(((i % SLIDES.length) + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    const t = setInterval(() => go(idx + 1), 6000);
    return () => clearInterval(t);
  }, [idx, go]);

  const slide = SLIDES[idx];

  return (
    <section data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden pt-28 pb-16">
      <div className="blob blob-float left-[-8%] top-[5%] h-[32rem] w-[32rem] bg-brand-blue/15" />
      <div className="blob blob-float-slow right-[-10%] top-[30%] h-[36rem] w-[36rem] bg-brand-sky/20" />
      <div className="blob blob-float bottom-[-20%] left-[30%] h-[26rem] w-[26rem] bg-brand-ice/80" />
      <div className="dot-grid absolute inset-0 opacity-[0.12]" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2">
        <AnimatePresence mode="wait">
          <motion.div
            key={slide.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.45 }}
            className="glass rounded-[28px] p-7 sm:p-10"
            data-testid={`hero-slide-${slide.id}`}
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-blue/20 bg-brand-ice/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-brand-blue">
              {slide.kicker}
            </span>
            <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.08] tracking-[-0.02em] text-brand-navy sm:text-5xl lg:text-[3.6rem]">
              {slide.headline.map((line, i) => (
                <span key={line} className="mask-line">
                  <span style={{ animationDelay: `${0.12 + i * 0.13}s` }}>{line}</span>
                </span>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.6 }}
              className="mt-5 max-w-lg text-base leading-relaxed text-brand-gray sm:text-lg"
            >
              {slide.sub}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Link to="/contact" data-testid="hero-cta-consultation" className="btn-gradient inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-brand-blue/30">
                Book Free Consultation <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/services" data-testid="hero-cta-services" className="inline-flex items-center gap-2 rounded-full border border-brand-blue/25 bg-white/60 px-7 py-3.5 text-sm font-semibold text-brand-blue backdrop-blur transition-all hover:-translate-y-0.5 hover:bg-brand-ice">
                Explore Services
              </Link>
            </motion.div>
            <div className="mt-8 flex items-center gap-3 text-xs text-brand-gray">
              <span className="font-semibold text-brand-navy">4.9/5</span> from 800+ founders & CFOs · Free 30-min consult, no obligation
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="relative hidden h-[440px] lg:block">
          <div className="glass absolute inset-0 rounded-[32px]" />
          <AnimatePresence mode="wait">
            <motion.div
              key={slide.id}
              initial={{ opacity: 0, scale: 0.94, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: -14 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 p-6"
            >
              <slide.Scene active />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <div className="relative mx-auto mt-10 flex max-w-7xl items-center justify-between px-4 sm:px-6">
        <div className="flex items-center gap-2.5">
          {SLIDES.map((s, i) => (
            <button
              key={s.id}
              data-testid={`hero-dot-${s.id}`}
              onClick={() => go(i)}
              aria-label={`Go to slide ${s.kicker}`}
              className={`h-2.5 rounded-full transition-all duration-500 ${i === idx ? "w-10 bg-gradient-to-r from-brand-blue to-brand-sky" : "w-2.5 bg-brand-blue/25 hover:bg-brand-blue/40"}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="hero-prev" onClick={() => go(idx - 1)} aria-label="Previous slide" className="glass flex h-11 w-11 items-center justify-center rounded-full text-brand-navy transition-all hover:-translate-y-0.5 hover:bg-brand-ice">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button data-testid="hero-next" onClick={() => go(idx + 1)} aria-label="Next slide" className="glass flex h-11 w-11 items-center justify-center rounded-full text-brand-navy transition-all hover:-translate-y-0.5 hover:bg-brand-ice">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </section>
  );
}
